import React from "react";
import Reveal from "@/components/landing/Reveal";
import Countdown from "@/components/landing/Countdown";
import EditorialButton from "@/components/landing/EditorialButton";
import { STRIPE_LINKS, EVENT } from "@/lib/landingConfig";
import { useReservation } from "@/components/landing/ReservationModal";

/**
 * Urgency — messaging changes based on availability:
 *
 * 1. Both modalities available + active promo (EVENT.code / windowStart):
 *    original "Nuevo lote de plazas" block with promo code and dates.
 * 2. Any modality sold out but promo still active:
 *    same block, but the sold-out modality shows "Aforo completo".
 * 3. Both modalities sold out (current default):
 *    "Se abrirán nuevas plazas próximamente" notice with waitlist CTAs.
 */
const Urgency = () => {
  const { open: openReservation } = useReservation();
  const bothClosed = EVENT.generalBatchClosed && EVENT.vipSoldOut;
  const hasPromo = Boolean(EVENT.code || EVENT.windowStart);

  // ---- Case: both closed — reopening-soon notice ----
  if (bothClosed) {
    return (
      <section
        id="urgencia"
        data-testid="urgency-section"
        className="bg-[#FAF8F3]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div
            className="relative border border-[#B77A4B]/40 bg-[#F4EFE7] p-8 md:p-14 lg:p-16"
            data-testid="urgency-card"
          >
            <span className="absolute -top-3 left-8 md:left-14 bg-[#B77A4B] text-[#FAF8F3] overline px-3 py-1.5">
              Reservas temporalmente cerradas
            </span>

            <div className="grid grid-cols-12 gap-8 md:gap-14 items-start">
              <div className="col-span-12 lg:col-span-7">
                <Reveal>
                  <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight text-[#3F403A]">
                    Se ha cerrado el segundo lote del Pase General y{" "}
                    <span className="italic text-[#B77A4B]">
                      el aforo VIP está completo.
                    </span>
                  </h2>
                </Reveal>
                <Reveal delay={140}>
                  <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-[#3F403A]/85 max-w-2xl">
                    Aún queda una oportunidad para acceder a la cuarta edición:
                    en unas semanas se anunciará el último lote del{" "}
                    <em className="not-italic font-medium">Pase General</em>.
                    La <em className="not-italic font-medium">Experiencia VIP</em>{" "}
                    ha completado aforo para esta edición.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="mt-4 font-sans text-base md:text-lg leading-relaxed text-[#3F403A]/85 max-w-2xl">
                    Únete a la lista de espera y te avisaremos en cuanto se
                    activen las nuevas plazas.
                  </p>
                </Reveal>
              </div>

              <div className="col-span-12 lg:col-span-5 space-y-6">
                <Reveal delay={140}>
                  <div
                    className="flex items-baseline justify-between border-t border-[#3F403A]/12 pt-6"
                    data-testid="urgency-general-seats"
                  >
                    <p className="overline text-[#3F403A]/70">
                      Pase General
                    </p>
                    <p className="font-serif italic text-xl md:text-2xl text-[#63846F] text-right leading-tight">
                      Último lote<br /><span className="text-[#63846F]/75 text-lg md:text-xl">en apertura</span>
                    </p>
                  </div>
                </Reveal>
                <Reveal delay={220}>
                  <div
                    className="flex items-baseline justify-between border-t border-[#3F403A]/12 pt-6"
                    data-testid="urgency-vip-seats"
                  >
                    <p className="overline text-[#3F403A]/70">
                      Experiencia VIP
                    </p>
                    <p className="font-serif italic text-2xl md:text-3xl text-[#B77A4B]">
                      Aforo completo
                    </p>
                  </div>
                </Reveal>
              </div>
            </div>

            <Reveal delay={200}>
              <div
                className="mt-10 md:mt-14 border-y border-[#3F403A]/12 py-8 md:py-10"
                data-testid="urgency-countdown"
              >
                <p className="overline text-[#3F403A]/60 mb-6 md:mb-8">
                  La jornada comienza en
                </p>
                <Countdown />
              </div>
            </Reveal>

            <Reveal delay={260}>
              <div className="mt-10 md:mt-14 flex flex-col sm:flex-row gap-4">
                <a
                  href="#info"
                  className="inline-flex items-center justify-center gap-3 rounded-none border border-[#63846F] bg-[#63846F] text-[#FAF8F3] hover:bg-[#4E6D5A] hover:border-[#4E6D5A] transition-colors px-8 py-4 md:px-10 md:py-5 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium"
                  data-testid="urgency-cta-general-waitlist"
                >
                  Avísame del último lote <span aria-hidden>→</span>
                </a>
                <a
                  href="#info"
                  className="inline-flex items-center justify-center gap-3 rounded-none border border-[#B77A4B] bg-transparent text-[#B77A4B] hover:bg-[#B77A4B] hover:text-[#FAF8F3] transition-colors px-8 py-4 md:px-10 md:py-5 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium"
                  data-testid="urgency-cta-vip-waitlist"
                >
                  Lista de espera VIP <span aria-hidden>→</span>
                </a>
              </div>
            </Reveal>

            <Reveal delay={320}>
              <p className="mt-8 max-w-2xl font-sans text-sm italic text-[#3F403A]/70">
                Las plazas son limitadas para preservar la calidad del encuentro,
                el networking y el acompañamiento posterior.
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    );
  }

  // ---- Case: at least one modality available (original block) ----
  return (
    <section
      id="urgencia"
      data-testid="urgency-section"
      className="bg-[#FAF8F3]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div
          className="relative border border-[#B77A4B]/40 bg-[#F4EFE7] p-8 md:p-14 lg:p-16"
          data-testid="urgency-card"
        >
          <span className="absolute -top-3 left-8 md:left-14 bg-[#B77A4B] text-[#FAF8F3] overline px-3 py-1.5">
            {hasPromo ? "Nuevo lote · Plazas limitadas" : "Plazas limitadas"}
          </span>

          <div className="grid grid-cols-12 gap-8 md:gap-14 items-start">
            <div className="col-span-12 lg:col-span-7">
              <Reveal>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight text-[#3F403A]">
                  {hasPromo && EVENT.windowStart && EVENT.windowEnd ? (
                    <>
                      Nuevo lote de plazas disponible solo del{" "}
                      <span className="text-[#B77A4B]">
                        {EVENT.windowStart} al {EVENT.windowEnd}.
                      </span>
                    </>
                  ) : (
                    <>Reserva tu plaza para la Edición Primer Aniversario.</>
                  )}
                </h2>
              </Reveal>
              {hasPromo && (
                <>
                  <Reveal delay={140}>
                    <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-[#3F403A]/85 max-w-2xl">
                      Abrimos un nuevo lote limitado para la Edición Primer
                      Aniversario de Lidera con Voz Propia
                      {EVENT.code
                        ? ". Durante estos días podrás reservar tu plaza aplicando el código especial:"
                        : "."}
                    </p>
                  </Reveal>
                  {EVENT.code && (
                    <Reveal delay={220}>
                      <div
                        className="mt-6 inline-flex items-center border border-[#B77A4B]/60 bg-[#FAF8F3] px-5 py-3"
                        data-testid="urgency-code"
                      >
                        <span className="overline text-[#3F403A]/60 mr-4">
                          Código
                        </span>
                        <span className="font-serif text-2xl md:text-3xl text-[#B77A4B] tracking-[0.15em]">
                          {EVENT.code}
                        </span>
                      </div>
                    </Reveal>
                  )}
                </>
              )}
            </div>

            <div className="col-span-12 lg:col-span-5 space-y-6">
              <Reveal delay={140}>
                <div
                  className="flex items-baseline justify-between border-t border-[#3F403A]/12 pt-6"
                  data-testid="urgency-general-seats"
                >
                  <p className="overline text-[#3F403A]/70">
                    Pase General · Plazas
                  </p>
                  {EVENT.generalBatchClosed ? (
                    <p
                      className="font-serif italic text-xl md:text-2xl text-[#63846F] text-right leading-tight"
                      data-testid="urgency-general-batch-closed"
                    >
                      Último lote en apertura
                    </p>
                  ) : (
                    <p className="font-serif text-5xl md:text-6xl text-[#63846F]">
                      {EVENT.seatsGeneral}
                    </p>
                  )}
                </div>
              </Reveal>
              <Reveal delay={220}>
                <div
                  className="flex items-baseline justify-between border-t border-[#3F403A]/12 pt-6"
                  data-testid="urgency-vip-seats"
                >
                  <p className="overline text-[#3F403A]/70">
                    Experiencia VIP · Plazas
                  </p>
                  {EVENT.vipSoldOut ? (
                    <p
                      className="font-serif italic text-2xl md:text-3xl text-[#B77A4B]"
                      data-testid="urgency-vip-soldout"
                    >
                      Aforo completo
                    </p>
                  ) : (
                    <p className="font-serif text-5xl md:text-6xl text-[#B77A4B]">
                      {EVENT.seatsVip}
                    </p>
                  )}
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={200}>
            <div
              className="mt-10 md:mt-14 border-y border-[#3F403A]/12 py-8 md:py-10"
              data-testid="urgency-countdown"
            >
              <p className="overline text-[#3F403A]/60 mb-6 md:mb-8">
                La jornada comienza en
              </p>
              <Countdown />
            </div>
          </Reveal>

          <Reveal delay={260}>
            <div className="mt-10 md:mt-14 flex flex-col sm:flex-row gap-4">
              {EVENT.generalBatchClosed ? (
                <a
                  href="#info"
                  className="inline-flex items-center justify-center gap-3 rounded-none border border-[#63846F] bg-transparent text-[#63846F] hover:bg-[#63846F] hover:text-[#FAF8F3] transition-colors px-8 py-4 md:px-10 md:py-5 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium"
                  data-testid="urgency-cta-general-waitlist"
                >
                  Avísame del último lote <span aria-hidden>→</span>
                </a>
              ) : (
                <EditorialButton
                  as="button"
                  type="button"
                  onClick={() => openReservation(STRIPE_LINKS.general, "Pase General")}
                  variant="jade"
                  data-testid="urgency-cta-general"
                >
                  Quiero mi Pase General
                </EditorialButton>
              )}
              {EVENT.vipSoldOut ? (
                <a
                  href="#info"
                  className="inline-flex items-center justify-center gap-3 rounded-none border border-[#B77A4B]/60 bg-transparent text-[#B77A4B] hover:bg-[#B77A4B] hover:text-[#FAF8F3] transition-colors px-8 py-4 md:px-10 md:py-5 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium"
                  data-testid="urgency-cta-vip-waitlist"
                >
                  Lista de espera VIP <span aria-hidden>→</span>
                </a>
              ) : (
                <EditorialButton
                  as="button"
                  type="button"
                  onClick={() => openReservation(STRIPE_LINKS.vip, "Experiencia VIP")}
                  variant="earth"
                  data-testid="urgency-cta-vip"
                >
                  Quiero vivir la Experiencia VIP
                </EditorialButton>
              )}
            </div>
          </Reveal>

          <Reveal delay={320}>
            <p className="mt-8 max-w-2xl font-sans text-sm italic text-[#3F403A]/70">
              Las plazas son limitadas para preservar la calidad del encuentro,
              el networking y el acompañamiento posterior.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Urgency;
